<template>
  <div class="company-details-display">
    <div class="row mb-3">
      <div class="col-sm-12">
        <div
          class="alert alert-primary"
          v-if="company.masterNote"
          v-html="company.masterNote">
        </div>
        <div v-if="!$route.meta.client" class="toolbar text-right">
          <dropdown
            :dropUp="false"
            :title="'Company Actions'"
            :icon="'fa fa-briefcase'"
            :actions="companyActions"
            @action="executeAction($event, company)">
          </dropdown>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <div class="card mb-5">
          <h4 class="card-header">
            <i class="fa fa-fw fa-briefcase"></i> Company Details
          </h4>
          <div class="card-body">
            <responsive-table
              :collapse="false"
              :hideColumns="true">
              <template slot="rows">
                <tr>
                  <td colspan="2">
                    <h4>{{ company.name }}</h4>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Phone</strong>
                  </td>
                  <td>{{ company.phone }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Email</strong>
                  </td>
                  <td>{{ company.email }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Website</strong>
                  </td>
                  <td>
                    <a v-if="website" :href="website" target="_blank">
                      View
                    </a>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <h4><i class="far fa-address-card"></i> Accounts Contact</h4>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Email</strong>
                  </td>
                  <td>{{ company.accountEmail }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Phone Number</strong>
                  </td>
                  <td>{{ company.accountPhone }}</td>
                </tr>
                <tr>
                  <td colspan="2">
                    <h4><i class="far fa-envelope"></i> Postal Address</h4>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Address</strong>
                  </td>
                  <td v-if="company.address">{{ company.address }}</td>
                  <td v-else>{{ company.addressEdit }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Suburb</strong>
                  </td>
                  <td>{{ company.suburb }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Postcode</strong>
                  </td>
                  <td>{{ company.postcode }}</td>
                </tr>
                <tr>
                  <td>
                    <strong>State</strong>
                  </td>
                  <td>{{ company.state }}</td>
                </tr>
                <tr>
                  <td colspan="2">
                    <h4><i class="far fa-map"></i> Office Address</h4>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    {{ company.officeAddress }}
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <h4><i class="fas fa-chart-line"></i> Activity Reports</h4>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <button class="my-2 btn btn-info" @click="executeAction('generateCompanyAcceptanceRateReport', company)">
                      <i class="fa fa-sticky-note" /> Generate Report
                    </button>
                    <button class="my-2 btn btn-primary" @click="executeAction('editCompanyAcceptanceRateReport', company)">
                      <i class="fa fa-fw fa-copy" /> View Reports
                    </button>
                  </td>
                </tr>
              </template>
            </responsive-table>
          </div>
        </div>
      </div>
      <div class="col-md-8">
        <nav-tabs>
          <tab name="Properties" icon="fa fa-building">
            <div class="text-right mb-3">
              <button class="btn btn-success" @click="executeAction('newProperty', company)">
                <i class="fa fa-plus"></i> New Property
              </button>
            </div>
            <search-form
              ref="companyPropertiesSearchForm"
              :results="companyProperties"
              :total-results="companyPropertiesTotal"
              :tableDisplay="true"
              :columns="companyPropertiesColumns"
              :options="propertiesForm">
            </search-form>
          </tab>
          <tab name="Managers" icon="fa fa-users">
            <div class="text-right mb-3">
              <button 
                class="btn btn-success"
                @click="executeAction('newCompanyManager', company)">
                <i class="fa fa-plus"></i> New Manager
              </button>
            </div>
            <search-form
              ref="companyManagersSearchForm"
              :results="companyManagers"
              :total-results="companyManagersTotal"
              :tableDisplay="true"
              :columns="companyManagersColumns"
              :options="managersForm">
            </search-form>
          </tab>
          <tab v-if="!$route.meta.client" name="Contractors" icon="fa fa-wrench">
            <div class="text-right mb-3">
              <button class="btn btn-success" @click="executeAction('newContractor', company)">
                <i class="fa fa-plus"></i> New Contractor
              </button>
            </div>
            <search-form
              ref="companyContractorsSearchForm"
              :results="companyContractors"
              :total-results="companyContractorsTotal"
              :tableDisplay="true"
              :columns="companyContractorsColumns"
              :options="contractorsForm">
            </search-form>
          </tab>
        </nav-tabs>
      </div>
    </div>
  </div>
</template>

<script>
  import Dropdown from '@/components/ui/Dropdown'
  import NavTabs from '@/components/ui/NavTab'
  import Tab from '@/components/ui/Tab'
  import SearchForm from '@/components/SearchForm'
  import ResponsiveTable from '@/components/ui/ResponsiveTable'
  import {
    EDIT_COMPANY,
    LOAD_COMPANY,
    NEW_COMPANY_MANAGER,
    NEW_CONTRACTOR,
    EDIT_MASTER_NOTE,
    NEW_PROPERTY,
    GENERATE_COMPANY_ACCEPTANCE_RATE_REPORT,
    EDIT_COMPANY_ACCEPTANCE_RATE_REPORT
  } from '@/vuex/types'
  import { mapGetters, mapActions } from 'vuex'

  export default {
    name: 'CompanyDetailsDisplay',
    components: {
      Dropdown,
      NavTabs,
      Tab,
      ResponsiveTable,
      SearchForm
    },
    computed: {
      website () {
        if (this.company.website && !this.company.website.toLowerCase().includes('http')) {
          return 'http://' + this.company.website
        } else {
          return this.company.website
        }
      },
      contractorsForm () {
        return {
          permalink: 'getCompanyContractors',
          method: 'post',
          params: {
            accountUuid: this.$route.params.companyUuid
          }
        }
      },
      managersForm () {
        return {
          permalink: 'getCompanyManagers',
          method: 'post',
          params: {
            accountUuid: this.$route.params.companyUuid
          }
        }
      },
      propertiesForm () {
        return {
          permalink: 'getCompanyProperties',
          method: 'post',
          params: {
            accountUuid: this.$route.params.companyUuid
          }
        }
      },
      company () {
        return {
          Accounts: [],
          Properties: [],
          Contractors: [],
          ...this.loadedCompany,
          ...this.updatedCompany
        }
      },
      ...mapGetters([
        'companyActions',
        'loadedCompany',
        'companyManagers',
        'companyManagersTotal',
        'companyManagersColumns',
        'companyProperties',
        'companyPropertiesTotal',
        'companyPropertiesColumns',
        'companyContractors',
        'companyContractorsTotal',
        'companyContractorsColumns'
      ])
    },
    data () {
      return {
        updatedCompany: {}
      }
    },
    watch: {
      $route () {
        this[LOAD_COMPANY](this.$route.params.companyUuid)
      }
    },
    mounted () {
      this[LOAD_COMPANY](this.$route.params.companyUuid)
    },
    methods: {
      async executeAction (action, payload) {
        let result = await this[action](payload)
        if (result && result.updated) this.updatedCompany = result.updated
      },
      ...mapActions([
        EDIT_COMPANY,
        LOAD_COMPANY,
        NEW_COMPANY_MANAGER,
        NEW_CONTRACTOR,
        EDIT_MASTER_NOTE,
        NEW_PROPERTY,
        GENERATE_COMPANY_ACCEPTANCE_RATE_REPORT,
        EDIT_COMPANY_ACCEPTANCE_RATE_REPORT
      ])
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/admin/CompanyDetailsDisplay.vue